import type {
  IFilterGroupOption,
  IPlacementSettings,
  ISliderArrowSettings,
  IVehicleDetailRow,
  IVehicleFilters,
} from '@/types'
import type {
  ICallToAction,
  IColorSettings,
  IIconSizeSettings,
  ITypography,
} from '@jazel/jazel-vuetify'
import { formatPlacementIdToFriendlyName } from '@/utils'

export const ensureLayoutSettings = (layoutSettings: any): any => {
  if (layoutSettings == null || typeof layoutSettings !== 'object') {
    return getDefaultLayoutSettings()
  }

  const newLayoutSettings = { ...layoutSettings }

  if (layoutSettings.type == null || layoutSettings.type === '') {
    newLayoutSettings.type = getDefaultLayoutSettings().type
  }

  if (
    layoutSettings.grid == null ||
    typeof layoutSettings.grid !== 'object' ||
    layoutSettings.grid.cardsPerRow == null ||
    layoutSettings.grid.cardsPerRow === ''
  ) {
    newLayoutSettings.grid = getDefaultLayoutSettings().grid
  }

  return newLayoutSettings
}

export const ensureActiveFilters = (
  activeFilters: IVehicleFilters | string | null,
): IVehicleFilters => {
  if (activeFilters == null || typeof activeFilters !== 'object') {
    return {}
  }

  const newActiveFilters = { ...activeFilters }

  for (const key of Object.keys(newActiveFilters)) {
    const activeFilter = newActiveFilters[key]

    if (!Array.isArray(activeFilter) || activeFilter.length === 0) {
      delete newActiveFilters[key]
    }
  }

  return newActiveFilters
}

export const ensureActiveFilterGroups = (activeFilterGroups: any): string => {
  const defaultValue = getDefaultActiveFilterGroups()

  if (activeFilterGroups == null || activeFilterGroups === '') {
    return defaultValue
  } else if (Array.isArray(activeFilterGroups)) {
    const joinedFilterGroups = activeFilterGroups.join(',')
    return joinedFilterGroups !== '' ? joinedFilterGroups : defaultValue
  } else if (typeof activeFilterGroups !== 'string') {
    return defaultValue
  }

  return activeFilterGroups
}

export const ensureAvailableFilterGroups = (
  availableFilterGroups: any,
): IFilterGroupOption[] => {
  const defaultValue = getDefaultAvailableFilterGroups()

  if (availableFilterGroups == null || availableFilterGroups === '') {
    return defaultValue
  } else if (typeof availableFilterGroups === 'string') {
    return availableFilterGroups.split(',').map((value: string) => {
      const trimmedValue = value.trim()

      return {
        title: trimmedValue.charAt(0).toUpperCase() + trimmedValue.slice(1),
        value: trimmedValue.toLowerCase(),
      }
    })
  }

  return availableFilterGroups
}

export const ensureVehicleDetailsRows = (
  vehicleDetailRows: any,
): IVehicleDetailRow[] => {
  const defaultValue = getDefaultVehicleFilterRows()
  const defaultColors = getDefaultVehicleFilterRowColors()
  const defaultTypography = getDefaultVehicleFilterRowTypography()

  if (vehicleDetailRows == null || typeof vehicleDetailRows !== 'object') {
    return defaultValue
  }

  const newRows = vehicleDetailRows
    .map((row: IVehicleDetailRow) => {
      // Check template
      if (row.template == null) {
        row.template = ''
      }

      // Check colors
      row.colors = ensureColors(defaultColors, row.colors)

      // Check typography
      if (row.typography == null) {
        row.typography = defaultTypography
      } else {
        row.typography = { ...defaultTypography, ...row.typography }
      }

      // Preserve the visible property (default to true if not set)
      if (row.visible === undefined) {
        row.visible = true
      }

      return row
    })
    .filter((row: IVehicleDetailRow) => row.template.trim() !== '')

  if (newRows.length === 0) {
    return defaultValue
  }

  return newRows
}

export const ensureCallToAction = (callToAction: any): ICallToAction => {
  const defaultValue = getDefaultCallToAction()
  const defaultColors = getDefaultCallToActionColors()
  const defaultTypography = getDefaultCallToActionTypography()
  const defaultIconColors = getDefaultCallToActionIconColors()
  const defaultIconSizes = getDefaultCallToActionIconSizes()

  if (callToAction == null || typeof callToAction !== 'object') {
    return defaultValue
  }

  const newCallToAction = { ...callToAction }

  // Check label
  if (callToAction.label == null || callToAction.label === '') {
    newCallToAction.label = defaultValue.label
  }

  // Check link
  if (callToAction.link == null || callToAction.link === '') {
    newCallToAction.link = defaultValue.link
  }

  // Check colors
  newCallToAction.colors = ensureColors(defaultColors, callToAction.colors)

  // Check icon
  if (callToAction.icon == null || typeof callToAction.icon !== 'object') {
    newCallToAction.icon = {
      colors: defaultIconColors,
      sizes: defaultIconSizes,
    }
  } else {
    // Check icon colors
    if (
      callToAction.icon.colors == null ||
      typeof callToAction.icon.colors !== 'object'
    ) {
      newCallToAction.icon.colors = defaultIconColors
    } else {
      newCallToAction.icon.colors = ensureColors(
        defaultIconColors,
        callToAction.icon.colors,
      )
    }

    // Check icon sizes
    if (
      callToAction.icon.sizes == null ||
      typeof callToAction.icon.sizes !== 'object'
    ) {
      newCallToAction.icon.sizes = defaultIconSizes
    } else {
      newCallToAction.icon.sizes = {
        ...defaultIconSizes,
        ...callToAction.icon.sizes,
      }
    }
  }

  // Check typography
  if (
    callToAction.typography == null ||
    typeof callToAction.typography !== 'object'
  ) {
    newCallToAction.typography = defaultTypography
  } else {
    newCallToAction.typography = {
      ...defaultTypography,
      ...callToAction.typography,
    }
  }

  return newCallToAction
}

export const ensureSliderArrowSettings = (sliderArrowSettings: any): any => {
  const defaultValue = getDefaultSliderArrowSettings()

  if (sliderArrowSettings == null || typeof sliderArrowSettings !== 'object') {
    return defaultValue
  }

  const newSliderArrowSettings = { ...sliderArrowSettings }

  if (
    sliderArrowSettings.fontSize == null ||
    sliderArrowSettings.fontSize === ''
  ) {
    newSliderArrowSettings.fontSize = defaultValue.fontSize
  }

  if (
    sliderArrowSettings.fontSizeUnit == null ||
    sliderArrowSettings.fontSizeUnit === ''
  ) {
    newSliderArrowSettings.fontSizeUnit = defaultValue.fontSizeUnit
  }

  if (
    sliderArrowSettings.colors == null ||
    typeof sliderArrowSettings.colors !== 'object'
  ) {
    newSliderArrowSettings.colors = defaultValue.colors
  } else {
    newSliderArrowSettings.colors = ensureColors(
      defaultValue.colors,
      sliderArrowSettings.colors,
    )
  }

  return newSliderArrowSettings
}

export const ensureCardSettings = (cardSettings: any): any => {
  const defaultValue = getDefaultCardSettings()

  if (cardSettings == null || typeof cardSettings !== 'object') {
    return defaultValue
  }

  const newCardSettings = { ...cardSettings }

  if (cardSettings.width == null || cardSettings.width === '') {
    newCardSettings.width = defaultValue.width
  }

  if (cardSettings.widthUnit == null || cardSettings.widthUnit === '') {
    newCardSettings.widthUnit = defaultValue.widthUnit
  }

  if (cardSettings.height == null || cardSettings.height === '') {
    newCardSettings.height = defaultValue.height
  }

  if (cardSettings.heightUnit == null || cardSettings.heightUnit === '') {
    newCardSettings.heightUnit = defaultValue.heightUnit
  }

  if (cardSettings.imageHeight == null || cardSettings.imageHeight === '') {
    newCardSettings.imageHeight = defaultValue.imageHeight
  }

  if (
    cardSettings.imageHeightUnit == null ||
    cardSettings.imageHeightUnit === ''
  ) {
    newCardSettings.imageHeightUnit = defaultValue.imageHeightUnit
  }

  if (cardSettings.margin == null || cardSettings.margin === '') {
    newCardSettings.margin = defaultValue.margin
  }

  if (cardSettings.marginUnit == null || cardSettings.marginUnit === '') {
    newCardSettings.marginUnit = defaultValue.marginUnit
  }

  if (cardSettings.borderRadius == null || cardSettings.borderRadius === '') {
    newCardSettings.borderRadius = defaultValue.borderRadius
  }

  if (
    cardSettings.borderRadiusUnit == null ||
    cardSettings.borderRadiusUnit === ''
  ) {
    newCardSettings.borderRadiusUnit = defaultValue.borderRadiusUnit
  }

  return newCardSettings
}

export const ensureActiveVehicles = (activeVehicles: any[]): string[] => {
  if (activeVehicles == null || !Array.isArray(activeVehicles)) {
    return []
  }

  return activeVehicles
}

export const ensureSortBy = (sortBy: string): string => {
  if (sortBy == null || sortBy === '') {
    return 'display_price'
  }

  return sortBy
}

export const ensureSortDirection = (sortDirection: string): string => {
  if (sortDirection == null || sortDirection === '') {
    return 'asc'
  }

  return sortDirection
}

export const getDefaultPlacementSettings = (id: string): IPlacementSettings => {
  return {
    enabled: true,
    friendlyName: formatPlacementIdToFriendlyName(id),
    url: '', // This will be overridden by the user input
    layout: {
      type: 'slider',
      grid: {
        cardsPerRow: 6,
      },
    },
    activeFilters: {},
    activeFilterGroups: getDefaultActiveFilterGroups(),
    availableFilterGroups: getDefaultAvailableFilterGroups(),
    numberOfVehicles: 4,
    activeVehicles: [],
    sortBy: 'display_price',
    sortDirection: 'asc',
    secondarySorts: [],
    vehicleDetailRows: getDefaultVehicleFilterRows(),
    callToAction: getDefaultCallToAction(),
    cardSettings: getDefaultCardSettings(),
    sliderArrowSettings: getDefaultSliderArrowSettings(),
  }
}

export const getDefaultLayoutSettings = (): any => {
  return {
    type: 'slider',
    grid: {
      cardsPerRow: 4,
    },
  }
}

export const getDefaultCardSettings = (): any => {
  return {
    width: '340',
    widthUnit: 'px',
    height: '380',
    heightUnit: 'px',
    imageHeight: '200',
    imageHeightUnit: 'px',
    margin: '4',
    marginUnit: 'px',
    borderRadius: '0',
    borderRadiusUnit: 'px',
  }
}

export const getDefaultSliderArrowSettings = (): ISliderArrowSettings => {
  return {
    fontSize: '48',
    fontSizeUnit: 'px',
    colors: {
      color: '#000000',
      colorHover: '#000000',
      background: 'transparent',
      backgroundHover: 'transparent',
    },
  }
}

export const getDefaultActiveFilterGroups = (): string => {
  return 'conditions, year, make, model, trim'
}

export const getDefaultAvailableFilterGroups = (): IFilterGroupOption[] => {
  return [
    { title: 'Conditions', value: 'conditions' },
    { title: 'Year', value: 'year' },
    { title: 'Make', value: 'make' },
    { title: 'Model', value: 'model' },
    { title: 'Trim', value: 'trim' },
  ]
}

export const getDefaultVehicleFilterRows = (): IVehicleDetailRow[] => {
  const defaultColors = getDefaultVehicleFilterRowColors()
  const defaultTypography = getDefaultVehicleFilterRowTypography()
  return [
    {
      template: '{{year}} {{make}} {{model}}',
      fieldType: 'basic',
      fields: ['year', 'make', 'model'],
      visible: true,
      colors: defaultColors,
      typography: { ...defaultTypography, fontSize: '20', fontWeight: 'bold' },
    },
    {
      template: '{{msrp|conditional|MSRP: ||}}',
      fieldType: 'conditional',
      conditionField: 'msrp',
      conditionPrefix: 'MSRP: ',
      hideValue: '',
      visible: true,
      colors: defaultColors,
      typography: defaultTypography,
    },
    {
      template: '{{price|conditional|||Call for Price}}',
      fieldType: 'conditional',
      conditionField: 'price',
      conditionPrefix: '',
      hideValue: 'Call for Price',
      visible: true,
      colors: { ...defaultColors, color: '#0EBD63', colorHover: '#0EBD63' },
      typography: { ...defaultTypography, fontSize: '20' },
    },
    {
      template: '{{mileage|conditional|||}}',
      fieldType: 'conditional',
      conditionField: 'mileage',
      conditionPrefix: '',
      hideValue: '',
      visible: true,
      colors: defaultColors,
      typography: defaultTypography,
    },
  ]
}

export const getDefaultVehicleFilterRowColors = (): IColorSettings => {
  return {
    color: '#000000',
    colorHover: '#000000',
    background: 'transparent',
    backgroundHover: 'transparent',
  }
}

export const getDefaultVehicleFilterRowTypography = (): ITypography => {
  return {
    fontFamily: 'inherit',
    fontSize: '16',
    fontSizeUnit: 'px',
    fontStyle: 'normal',
    fontWeight: 'normal',
    letterSpacing: '0',
    letterSpacingUnit: 'px',
    lineHeight: '1.5',
    lineHeightUnit: 'em',
    textDecoration: 'none',
    textTransform: 'none',
    textAlignment: 'left',
  }
}

export const getDefaultCallToAction = (): ICallToAction => {
  return {
    label: 'View Details',
    link: '/vehicle/{{vin}}',
    colors: getDefaultCallToActionColors(),
    icon: {
      colors: getDefaultCallToActionIconColors(),
      sizes: getDefaultCallToActionIconSizes(),
    },
    typography: getDefaultCallToActionTypography(),
    border: {
      width: '1',
      widthUnit: 'px',
      style: 'solid',
      color: '#000',
      hoverColor: '#000',
      radius: '0',
      radiusUnit: 'px',
    },
  }
}

export const getDefaultCallToActionColors = (): IColorSettings => {
  return {
    color: '#000000',
    colorHover: '#000000',
    background: 'transparent',
    backgroundHover: 'transparent',
  }
}

export const getDefaultCallToActionIconColors = (): IColorSettings => {
  return {
    color: '#000000',
    colorHover: '#000000',
    background: 'transparent',
    backgroundHover: 'transparent',
  }
}

export const getDefaultCallToActionIconSizes = (): IIconSizeSettings => {
  return {
    container: '30',
    containerUnit: 'px',
    icon: '30',
    iconUnit: 'px',
  }
}

export const getDefaultCallToActionTypography = (): ITypography => {
  return {
    fontFamily: 'inherit',
    fontSize: '14',
    fontSizeUnit: 'px',
    fontStyle: 'normal',
    fontWeight: 'normal',
    letterSpacing: '0',
    letterSpacingUnit: 'px',
    lineHeight: '1.5',
    lineHeightUnit: 'em',
    textDecoration: 'none',
    textTransform: 'none',
    textAlignment: 'left',
  }
}

const ensureColors = (
  defaultColors: IColorSettings,
  colors: any,
): IColorSettings => {
  const newColors = { ...(colors ?? {}) }

  if (colors == null) {
    return defaultColors
  } else {
    if (colors.color == null) {
      newColors.color = defaultColors.color
    }

    if (colors.colorHover == null) {
      newColors.colorHover = newColors.color
    }

    if (colors.background == null) {
      newColors.background = defaultColors.background
    }

    if (colors.backgroundHover == null) {
      newColors.backgroundHover = newColors.background
    }
  }

  return newColors
}

export const getSampleItem = (): { [key: string]: any } => {
  return {
    account_id: '1147',
    account_name: 'Seth Wadley Chevrolet Buick of Ada',
    auto_check: '0',
    carfax_key_hash: 'Y3SJApHI9m7xSaqGror9xw==',
    carfax_one_owner: '0',
    certified_by_dealer: '0',
    certified_by_manufacturer: '0',
    condition: 'Used',
    conditions: ['Used'],
    date_in_stock: '2024-10-23T00:00:00Z',
    date_in_stock_ts: '1729641600',
    dealer_videos: '[]',
    discount: '0.0',
    display_price: '12500.0',
    drivetrain: 'Front Wheel Drive',
    drivetrain_code: 'FWD',
    engine: '3.5L V6 Ti-VCT',
    engine_cylinders: '6 Cylinders',
    expando_srp: "{'Wheelbase':'112.8'}",
    exterior_color: 'White',
    exterior_factory_color: 'Oxford White',
    fuel_type: 'Gasoline',
    fuel_types: ['Gasoline'],
    generic_text_2_literal: ['6-Speed Automatic with Select-Shift'],
    generic_text_3_literal: ['3.5L V6 Ti-VCT'],
    has_carfax: '0',
    id: '4023940',
    image:
      "{'Id':263812563,'VisibleShortUrl':'/media/263812563','Base64':'/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAP///////////////////////////////////////////…",
    incentives_structured_stacks: '[]',
    interior_color_sidebar: 'Unknown',
    interior_factory_color: 'Medium Stone',
    location_address1: '1301 N Broadway Ave',
    location_city: 'Ada',
    location_city_state_sidebar: 'Ada, OK',
    location_contact_number: '580-279-6749',
    location_state: 'OK',
    location_zipcode: '74820',
    make: 'Ford',
    mileage: '129107',
    model: 'Explorer',
    mpg_city_average: '17',
    mpg_city_high: '17',
    mpg_city_low: '17',
    mpg_highway_average: '24',
    mpg_highway_high: '24',
    mpg_highway_low: '24',
    packages: [
      "{'Standard':false,'Price':0,'Name':['Medium Stone,…RelatedColor','Code':'7L','Category':'SEAT TYPE'}",
      "{'Standard':false,'Price':0,'Name':['Engine: 3.5L …Cause':'Engine','Code':'998','Category':'ENGINE'}",
      "{'Standard':false,'Price':0,'Name':['Oxford White'…tedColor','Code':'YZ','Category':'PRIMARY PAINT'}",
    ],
    photo_count: '29',
    state: 'On Lot',
    stock_number: 'LD3035',
    transmission: 'Automatic',
    transmission_description: '6-Speed Automatic with Select-Shift',
    trim: 'Base',
    used: '1',
    vehicle_highlights: [
      'MEDIUM STONE, CLOTH BUCKET SEATS',
      'ENGINE: 3.5L TI-VCT V6',
      'OXFORD WHITE',
    ],
    vehicle_type: ['Sport Utility', 'SUV / Crossover'],
    video_count: '0',
    vin: '1FM5K7B89JGA54767',
    year: '2018',
  }
}

export const getLastMonthFirstDay = (): string => {
  const date = new Date()
  date.setDate(1)
  date.setMonth(date.getMonth() - 1)

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = '01'

  return `${year}-${month}-${day}`
}

export const getLastMonthLastDay = (): string => {
  const date = new Date()
  date.setDate(0)

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

export const getCurrentMonthFirstDay = (): string => {
  const date = new Date()
  date.setDate(1)

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = '01'

  return `${year}-${month}-${day}`
}

export const getCurrentMonthLastDay = (): string => {
  const date = new Date()
  date.setMonth(date.getMonth() + 1)
  date.setDate(0)

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

export const formatDateString = (date: string) => {
  const d = new Date(date)
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const calculatePercentageChange = (
  current: number,
  previous: number,
) => {
  if (previous === 0) return Math.round(current * 100)
  return Math.round(((current - previous) / previous) * 100)
}

export const formatDate = (date: Date): string => {
  // Return YYYY-MM-DD in local time to avoid UTC offset shifting
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const getFirstDayOfLastMonthAsDate = (): Date => {
  const date = new Date()
  date.setMonth(date.getMonth() - 1, 1) // First day of last month
  return date
}

export const getLastDayOfLastMonthAsDate = (): Date => {
  const date = new Date()
  date.setMonth(date.getMonth(), 0) // Last day of previous month
  return date
}

export const getFirstDayOfCurrentMonthAsDate = (): Date => {
  const date = new Date()
  date.setDate(1)
  return date
}

export const getLastDayOfCurrentMonthAsDate = (): Date => {
  const date = new Date()
  date.setMonth(date.getMonth() + 1, 0) // Last day of current month
  return date
}

export const getCurrentMonthBounds = () => {
  const now = new Date()

  // First day of current month
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
  firstDay.setHours(0, 0, 0, 0)

  // Last day of current month
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  lastDay.setHours(23, 59, 59, 999)

  return {
    start: firstDay,
    end: lastDay,
  }
}

export const getLastMonthBounds = () => {
  const now = new Date()

  // First day of last month
  const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  firstDay.setHours(0, 0, 0, 0)

  // Last day of last month
  const lastDay = new Date(now.getFullYear(), now.getMonth(), 0)
  lastDay.setHours(23, 59, 59, 999)

  return {
    start: firstDay,
    end: lastDay,
  }
}

export const getCurrentWeekBounds = () => {
  const now = new Date()
  const currentDay = now.getDay() // 0 is Sunday, 1 is Monday, etc.

  // Calculate Monday (start of week)
  // If today is Sunday (0), we need to go back 6 days
  // For all other days, we go back (currentDay - 1) days
  const monday = new Date(now)
  monday.setDate(now.getDate() - (currentDay === 0 ? 6 : currentDay - 1))
  monday.setHours(0, 0, 0, 0)

  // Calculate Sunday (end of week)
  // Add days needed to reach Sunday
  const sunday = new Date(now)
  sunday.setDate(now.getDate() + (currentDay === 0 ? 0 : 7 - currentDay))
  sunday.setHours(23, 59, 59, 999)

  return {
    start: monday,
    end: sunday,
  }
}

export const getLastWeekBounds = () => {
  const now = new Date()
  const currentDay = now.getDay()

  // Calculate last Monday by going back to this week's Monday, then 7 more days
  const lastMonday = new Date(now)
  lastMonday.setDate(
    now.getDate() - (currentDay === 0 ? 6 : currentDay - 1) - 7,
  )
  lastMonday.setHours(0, 0, 0, 0)

  // Calculate last Sunday by going back to this week's Monday, then 1 day
  const lastSunday = new Date(now)
  lastSunday.setDate(
    now.getDate() - (currentDay === 0 ? 6 : currentDay - 1) - 1,
  )
  lastSunday.setHours(23, 59, 59, 999)

  return {
    start: lastMonday,
    end: lastSunday,
  }
}

export const compareDates = (
  date1: Date | undefined,
  date2: Date | undefined,
) => {
  if (!date1 || !date2) return false

  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  )
}
